import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Motion";
import { CONTACT } from "@/i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIME_SLOTS = ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.16em] text-navy/60 font-semibold">{label}</span>
    {children}
  </label>
);

const Contact = () => {
  const { t } = useLang();
  const c = t.contactPage;
  const [tab, setTab] = useState("message");
  const [loading, setLoading] = useState(false);

  const [msg, setMsg] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [appt, setAppt] = useState({ name: "", email: "", phone: "", company: "", service: "", date: "", time: "", notes: "" });

  const submitMessage = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, msg);
      toast.success(c.successMsg);
      setMsg({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (err) {
      toast.error(c.errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const submitAppt = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/appointment`, appt);
      toast.success(c.successBook);
      setAppt({ name: "", email: "", phone: "", company: "", service: "", date: "", time: "", notes: "" });
    } catch (err) {
      toast.error(c.errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: Phone, label: c.info.phone, value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}` },
    { icon: Mail, label: c.info.email, value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: MapPin, label: c.info.web, value: CONTACT.web, href: null },
  ];

  return (
    <div data-testid="contact-page">
      <PageHeader eyebrow={c.eyebrow} title={c.title} lead={c.lead} />

      <section className="py-24 lg:py-32 bg-ivory">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
          {/* Left: contact info */}
          <div className="lg:col-span-4">
            <Reveal>
              <div className="space-y-8">
                {info.map((it) => (
                  <div key={it.label} className="border-b border-navy/10 pb-6">
                    <div className="flex items-center gap-3 text-gold mb-3">
                      <it.icon size={18} strokeWidth={1.5} />
                      <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-navy/60">{it.label}</span>
                    </div>
                    {it.href ? (
                      <a href={it.href} className="font-serif text-2xl text-navy hover:text-gold transition-colors break-all" data-testid={`contact-info-${it.label}`}>{it.value}</a>
                    ) : (
                      <span className="font-serif text-2xl text-navy">{it.value}</span>
                    )}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Right: forms */}
          <div className="lg:col-span-8 lg:col-start-5">
            <div className="flex gap-2 mb-10 border-b border-navy/15">
              {[["message", c.tabMessage], ["book", c.tabBook]].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  data-testid={`tab-${key}`}
                  className={`relative px-6 py-4 text-[13px] uppercase tracking-[0.16em] font-semibold transition-colors ${tab === key ? "text-navy" : "text-navy/40 hover:text-navy/70"}`}
                >
                  {label}
                  {tab === key && <motion.span layoutId="tab-underline" className="absolute left-0 right-0 -bottom-px h-[2px] bg-gold" />}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {tab === "message" ? (
                <motion.form
                  key="message"
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4 }}
                  onSubmit={submitMessage}
                  className="grid sm:grid-cols-2 gap-x-8 gap-y-7"
                  data-testid="contact-form"
                >
                  <Field label={c.form.name}>
                    <input required value={msg.name} onChange={(e) => setMsg({ ...msg, name: e.target.value })} className="lux-input w-full" data-testid="input-name" />
                  </Field>
                  <Field label={c.form.email}>
                    <input required type="email" value={msg.email} onChange={(e) => setMsg({ ...msg, email: e.target.value })} className="lux-input w-full" data-testid="input-email" />
                  </Field>
                  <Field label={c.form.company}>
                    <input value={msg.company} onChange={(e) => setMsg({ ...msg, company: e.target.value })} className="lux-input w-full" data-testid="input-company" />
                  </Field>
                  <Field label={c.form.subject}>
                    <input value={msg.subject} onChange={(e) => setMsg({ ...msg, subject: e.target.value })} className="lux-input w-full" data-testid="input-subject" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label={c.form.message}>
                      <textarea required rows={4} value={msg.message} onChange={(e) => setMsg({ ...msg, message: e.target.value })} className="lux-input w-full resize-none" data-testid="input-message" />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <button type="submit" disabled={loading} className="btn-primary group inline-flex items-center gap-3 px-10 py-4 text-[13px] uppercase tracking-[0.18em] font-semibold disabled:opacity-60" data-testid="submit-contact">
                      {loading ? <><Loader2 size={16} className="animate-spin" /> {c.form.sending}</> : <>{c.form.send} <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" /></>}
                    </button>
                  </div>
                </motion.form>
              ) : (
                <motion.form
                  key="book"
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4 }}
                  onSubmit={submitAppt}
                  className="grid sm:grid-cols-2 gap-x-8 gap-y-7"
                  data-testid="booking-form"
                >
                  <Field label={c.form.name}>
                    <input required value={appt.name} onChange={(e) => setAppt({ ...appt, name: e.target.value })} className="lux-input w-full" data-testid="book-name" />
                  </Field>
                  <Field label={c.form.email}>
                    <input required type="email" value={appt.email} onChange={(e) => setAppt({ ...appt, email: e.target.value })} className="lux-input w-full" data-testid="book-email" />
                  </Field>
                  <Field label={c.form.phone}>
                    <input value={appt.phone} onChange={(e) => setAppt({ ...appt, phone: e.target.value })} className="lux-input w-full" data-testid="book-phone" />
                  </Field>
                  <Field label={c.form.company}>
                    <input value={appt.company} onChange={(e) => setAppt({ ...appt, company: e.target.value })} className="lux-input w-full" data-testid="book-company" />
                  </Field>
                  <Field label={c.form.service}>
                    <select required value={appt.service} onChange={(e) => setAppt({ ...appt, service: e.target.value })} className="lux-input w-full bg-transparent" data-testid="book-service">
                      <option value="" disabled>{c.form.selectService}</option>
                      {t.services.map((s) => <option key={s.title} value={s.title}>{s.title}</option>)}
                    </select>
                  </Field>
                  <Field label={c.form.date}>
                    <input required type="date" value={appt.date} onChange={(e) => setAppt({ ...appt, date: e.target.value })} className="lux-input w-full bg-transparent" data-testid="book-date" />
                  </Field>
                  <Field label={c.form.time}>
                    <select required value={appt.time} onChange={(e) => setAppt({ ...appt, time: e.target.value })} className="lux-input w-full bg-transparent" data-testid="book-time">
                      <option value="" disabled>{c.form.selectTime}</option>
                      {TIME_SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label={c.form.notes}>
                      <textarea rows={3} value={appt.notes} onChange={(e) => setAppt({ ...appt, notes: e.target.value })} className="lux-input w-full resize-none" data-testid="book-notes" />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <button type="submit" disabled={loading} className="btn-primary group inline-flex items-center gap-3 px-10 py-4 text-[13px] uppercase tracking-[0.18em] font-semibold disabled:opacity-60" data-testid="submit-booking">
                      {loading ? <><Loader2 size={16} className="animate-spin" /> {c.form.sending}</> : <>{c.form.book} <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" /></>}
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
